import { APP_DEFAULT_PATH } from 'config';

// Every role that lands on the admin experience. The three Admin Persona levels
// differ in what they may DO, not in where they start — all three open on the
// admin dashboard, and the left menu (menu-items/adminLevels.js) plus the route
// guards are what separate them.
export const ADMIN_LANDING_ROLES = ['super_admin', 'ayro_it_admin', 'ayro_admin', 'org_admin'];

// Roles that administer a whole organization rather than holding a caseload.
//
// Several clinical screens narrow themselves to "my children" by passing the
// signed-in person's id as a supervisor filter. That is right for a supervisor
// and wrong for an administrator, who supervises nobody and would therefore see
// an empty list even with an organization full of children. Screens that do
// that filtering should skip it for these roles and let the API's own
// organization scoping decide what is visible.
//
// Deliberately excludes center_admin: a centre administrator's reach is
// narrower than the organization, and widening it is a decision in its own
// right rather than a side effect of this fix.
// 'organization_administrator' is the code a customer's own admin role was created
// with in Role Definitions. This list decides whether someone is scoped to the whole
// organization or to one supervisor's caseload, so a role missing from it silently
// gets the narrow view - the child and therapist pickers come back short rather than
// erroring. Listed as a stopgap alongside the same addition to the two backend
// RoleChecker lists (product_tiers.py, analytics.py); the durable fix is to decide
// this from a granted permission rather than from the role's name.
export const ORG_WIDE_ADMIN_ROLES = ['super_admin', 'ayro_it_admin', 'ayro_admin', 'org_admin', 'organization_administrator'];

export const isOrgWideAdmin = (role) => ORG_WIDE_ADMIN_ROLES.includes(role?.toLowerCase?.());

// Resolve the post-login landing path for a user based on their role. Kept in
// one place so AuthLogin (explicit redirect on submit), GuestGuard (effect on
// refresh / token rehydrate), and the MFA flow can't drift out of sync.
export function getDefaultPathForRole(role, myMenu = null) {
  const userRole = role?.toLowerCase?.();

  // Land by PERSONA (the template a role was created from — menu_template), falling back
  // to the role's own code. A role cloned from Therapist lands on the therapist home,
  // not a generic template route. Existing persona roles have menu_template = their code.
  const persona = (myMenu?.menu_template || userRole)?.toLowerCase?.();

  // A role with an unknown persona (e.g. one whose menu_template was never set) falls
  // through to the first screen the server says it actually grants, rather than to a
  // leftover template route.
  const granted = myMenu?.landing;
  const KNOWN = [
    ...ADMIN_LANDING_ROLES,
    'supervisor',
    'admin',
    'therapist',
    'inclusionhead',
    'clinicalhead',
    'bcba',
    'center_admin',
    'parent'
  ];
  if (granted && persona && !KNOWN.includes(persona)) return granted;
  if (ADMIN_LANDING_ROLES.includes(persona)) return '/admin/dashboard';
  // bcba (BCBA Head) and center_admin share the supervisor experience.
  if (persona === 'supervisor' || persona === 'admin' || persona === 'bcba' || persona === 'center_admin') return '/supervisor/dashboard';
  if (persona === 'therapist') return '/therapist/dashboard';
  if (persona === 'inclusionhead') return '/inclusionhead/child-profiles';
  if (persona === 'clinicalhead') return '/clinicalhead/dashboard';
  // The Caregiver persona (role code `parent`). Their own dashboard, NOT the
  // server-chosen landing: that is picked from the catalogue's feature routes, which are
  // supervisor- and admin-prefixed, and it used to drop a caregiver on /admin/dashboard.
  // The dashboard is the caregiver's home — it lists their child with a "View Profile"
  // entry into the gated child-profile tabs, so the standalone child list is no longer a
  // menu item.
  if (persona === 'parent') return '/caregiver/dashboard';
  return APP_DEFAULT_PATH;
}
